import {
  hqHalfyearDocumentSchema,
  hqHalfyearMetricCatalog,
  type HqHalfyearDocument,
  type HqHalfyearMetricValue,
} from '@kop/contracts';
import { assertPrintable, type PrintSection, type PrintViewModel } from '@kop/print';

export const hqHalfyearStatusLabels = { pending: '미검토', reviewing: '검토 중', followup: '보완 요청', completed: '검토 완료' } as const;

export function halfyearSourceReason(reason: string | null | undefined): string {
  const labels: Record<string, string> = {
    'source-missing': '저장된 자료 없음',
    'source-binding-or-date-invalid': '사업장·기간 확인 필요',
    'source-generation-pending-or-invalid': '자료 집계 확인 필요',
    'source-year-mismatch': '선택 연도의 자료 없음',
    'source-module-unavailable': '해당 기능 자료 미확인',
    'source-metric-incomplete': '자료 일부 누락',
    'source-metric-unavailable': '해당 수치 미확인',
  };
  return reason ? labels[reason] ?? '자료 확인 필요' : '자료 미확인';
}

export function halfyearMetricValue(metric: HqHalfyearMetricValue | undefined, unit: string): string {
  return metric?.status === 'available' && metric.value !== null ? `${metric.value.toLocaleString('ko-KR')} ${unit}` : '자료 미확인';
}

export function halfyearSourcePeriod(metric: HqHalfyearMetricValue | undefined): string {
  if (!metric?.actualPeriod) return '기간 미확인';
  const { start, end, kind } = metric.actualPeriod;
  return `${start} ~ ${end} · ${kind === 'annual' ? '연간' : '월간'} 자료`;
}

/** Only an acknowledged server document can enter the saved-version print pipeline. */
export function buildHqHalfyearPrint(input: HqHalfyearDocument, actorId: string, generatedAt = new Date().toISOString()): PrintViewModel {
  const record = hqHalfyearDocumentSchema.parse(input);
  const { draft } = record;
  const sections: PrintSection[] = [{
    kind: 'fields', id: 'saved-basis', title: '저장 기준', fields: [
      { key: 'period', label: '점검 기간', value: `${draft.year}년 ${draft.half === 1 ? '상반기' : '하반기'}` },
      { key: 'cutoff', label: '자료 기준일', value: draft.cutoff },
      { key: 'saved', label: '저장 시각', value: record.savedAt },
      { key: 'version', label: '저장본', value: record.version },
    ],
  }, {
    kind: 'narrative', id: 'working-notice', title: '검토용 저장본',
    body: '작성 중인 반기점검의 저장본입니다. 제출·승인본이 아닙니다. 연간·월간 참고 자료는 반기 누계가 아니며, 자동 연결 수치는 의무 이행이나 점검 완료를 판정하지 않습니다.',
  }];
  for (const [index, topic] of draft.topics.entries()) {
    if (index) sections.push({ kind: 'page-break', id: `break-${topic.id}` });
    sections.push({ kind: 'fields', id: `topic-${topic.id}`, title: `${String(index + 1).padStart(2, '0')}. ${topic.label}`, fields: [
      { key: 'status', label: '검토 상태', value: hqHalfyearStatusLabels[topic.status] },
      { key: 'responsible', label: '담당자', value: topic.responsible || '미지정' },
      { key: 'dueDate', label: '기한', value: topic.dueDate || '미지정' },
    ] },
    { kind: 'narrative', id: `implementation-${topic.id}`, title: '본사 수행', body: topic.implementationNotes || '기록 없음' },
    { kind: 'narrative', id: `conclusion-${topic.id}`, title: '점검 결과·후속조치', body: topic.conclusion || '기록 없음' });
    for (const siteId of draft.siteIds) {
      const source = record.siteSnapshots.find(item => item.siteId === siteId);
      const notes = topic.sites.find(item => item.siteId === siteId);
      sections.push({ kind: 'narrative', id: `notes-${topic.id}-${siteId}`, title: source?.name ?? siteId,
        body: `증빙 확인: ${notes?.evidenceNotes || '기록 없음'}\n검토 의견: ${notes?.reviewNotes || '기록 없음'}` });
      if (topic.metricIds.length) sections.push({
        kind: 'table', id: `metrics-${topic.id}-${siteId}`, title: '저장 당시 참고 자료', repeatHeader: true,
        columns: [ { key: 'label', label: '자료', valueType: 'text' }, { key: 'value', label: '값', valueType: 'text' }, { key: 'basis', label: '기간·원본 저장 시각', valueType: 'text' } ],
        rows: topic.metricIds.map(id => {
          const entry = hqHalfyearMetricCatalog.find(item => item.id === id)!;
          const metric = source?.metrics.find(item => item.id === id);
          return { id, cells: { label: entry.label, value: halfyearMetricValue(metric, entry.unit), basis: `${halfyearSourcePeriod(metric)}\n${metric?.generatedAt ?? '저장 시각 미확인'}${metric?.status === 'unavailable' ? `\n${halfyearSourceReason(metric.reason)}` : ''}` } };
        }),
      });
    }
  }
  const model: PrintViewModel = {
    schemaVersion: '1.0', identity: { documentType: 'hq-halfyear-review', documentId: record.id, revisionId: `${record.id}:v${record.version}`, versionLabel: `저장본 ${record.version}` },
    title: `${draft.year}년 ${draft.half === 1 ? '상반기' : '하반기'} 안전보건 반기점검`,
    subtitle: '본사 · 검토용 저장본', locale: 'ko-KR', purpose: 'working-copy', publicationStatus: 'working-copy', pageProfileId: 'a4-portrait-evidence',
    generatedAt, generatedBy: { actorId, displayName: '본사 조회 담당자', organizationName: '본사' },
    target: { capturedAt: record.savedAt, recordIds: [record.id], declaredRecordCount: 1, sourceVersion: `${record.version}:${record.contentHash}`, filterSummary: [{ key: 'cutoff', label: '자료 기준일', value: draft.cutoff }] },
    privacy: { mode: 'full', redactedFieldKeys: [] }, assets: [], sections, warnings: [],
  };
  assertPrintable(model);
  return model;
}
